/**
 * RingOverlay — an incoming "call" from the assistant (owner directive 2026-09-30).
 *
 * The assistant can ring the user when it genuinely needs them. The ring rides
 * the existing in-app notification SSE channel as a notification of type
 * "assistant_ring"; useNotifications re-dispatches it as a window "saik:ring"
 * event, and this overlay answers it everywhere in the app — including the iOS
 * app, which is a WKWebView wrapper around this same web app.
 *
 * Audio autoplay: browsers require a prior user gesture to play sound; when audio
 * is blocked we still show the full-screen ring, vibrate, and speak (best-effort).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, PhoneOff } from 'lucide-react';
import { resolveNotificationUrl } from './NotificationBell';
import { ringsApi } from '../api/rings';

export interface RingPayload { id?: number; title?: string; body?: string; url?: string; }

// Owner directive 2026-10-10: a ring rings for only SIX seconds. If you don't
// answer, it becomes a MISSED call and you return to the regular dashboard (no
// call starts). Declining (Dismiss) pops the agent up — no call — and keeps you
// on the regular dashboard.
const RING_SECONDS = 6;

export default function RingOverlay() {
  const [ring, setRing] = useState<RingPayload | null>(null);
  const [countdown, setCountdown] = useState(RING_SECONDS);
  const [askEnable, setAskEnable] = useState(false);
  const ringRef = useRef<RingPayload | null>(null);
  ringRef.current = ring;
  const audioRef = useRef<AudioContext | null>(null);
  const loopRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: Event) => {
      // Mid-call guard (owner directive 2026-10-12): never hijack the screen with
      // a new incoming-call popup while a live voice session is open — the user
      // is already talking. The green ringer bar still surfaces other ringers.
      if (document.documentElement.dataset.saikLiveVoice === '1') return;
      setRing(((e as CustomEvent).detail || {}) as RingPayload);
    };
    window.addEventListener('saik:ring', handler as EventListener);
    return () => window.removeEventListener('saik:ring', handler as EventListener);
  }, []);

  useEffect(() => {
    if (!ring) return;
    const buzz = (p: number[]) => { try { (navigator as any).vibrate?.(p); } catch { /* ignore */ } };
    buzz([500, 250, 500, 250, 500]);
    try {
      if (typeof speechSynthesis !== 'undefined' && (ring.body || ring.title)) {
        const u = new SpeechSynthesisUtterance(ring.body || ring.title || '');
        u.rate = 1; window.speechSynthesis.speak(u);
      }
    } catch { /* ignore */ }

    const beep = () => {
      try {
        const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (!Ctx) return;
        if (!audioRef.current) audioRef.current = new Ctx();
        const ctx = audioRef.current!;
        if (ctx.state === 'suspended') ctx.resume().catch(() => {});
        const t0 = ctx.currentTime;
        [[0, 560], [0.45, 720]].forEach(([off, freq]) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.type = 'sine'; o.frequency.value = freq;
          g.gain.setValueAtTime(0.0001, t0 + off);
          g.gain.exponentialRampToValueAtTime(0.28, t0 + off + 0.04);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + off + 0.38);
          o.connect(g); g.connect(ctx.destination);
          o.start(t0 + off); o.stop(t0 + off + 0.4);
        });
      } catch { /* ignore */ }
    };
    beep();
    loopRef.current = setInterval(beep, 1600);
    const vibLoop = setInterval(() => buzz([500, 250, 500]), 3200);
    // Six-second ring window: when it elapses with no answer, it's a MISSED call
    // (owner directive 2026-10-10) — stop ringing and return to the regular
    // dashboard. No call starts; the green active-ring bar still offers Answer.
    setCountdown(RING_SECONDS);
    const tick = setInterval(() => {
      setCountdown((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(loopRef.current ?? undefined); loopRef.current = null;
          clearInterval(vibLoop);
          clearInterval(tick);
          try { audioRef.current?.suspend(); } catch { /* ignore */ }
          try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
          const r = ringRef.current;
          if (r?.id != null) { try { localStorage.setItem('saik_last_ring_id', String(r.id)); } catch { /* ignore */ } }
          try { window.dispatchEvent(new CustomEvent('saik:ring-missed', { detail: r })); } catch { /* ignore */ }
          setRing(null);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => {
      if (loopRef.current) clearInterval(loopRef.current);
      clearInterval(vibLoop);
      clearInterval(tick);
      try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
      try { audioRef.current?.suspend(); } catch { /* ignore */ }
    };
  }, [ring]);

  if (!ring) return null;

  const stop = () => {
    if (loopRef.current) clearInterval(loopRef.current);
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
    try { (navigator as any).vibrate?.(0); } catch { /* ignore */ }
    setRing(null);
  };
  const answer = () => {
    const r = ring;
    // OWNER RULE (2026-10-13 23:33): if the widget is turned OFF in the user's
    // settings, accepting a call must first offer to turn the widget ON. Yes →
    // enable the widget and start the call; already on → the call just starts.
    try {
      const widgetOn = document.documentElement.dataset.saikWidgetEnabled === '1';
      if (!widgetOn) { stop(); setAskEnable(true); return; }
    } catch { /* ignore — fall through to a normal answer */ }
    stop();
    try { localStorage.setItem('saik_last_ring_id', String(r.id ?? '')); } catch { /* ignore */ }
    // Active Ringers (2026-10-10): track the answer server-side (it stays active).
    if (r.id != null) { void ringsApi.answer(r.id).catch(() => {}); }
    try { window.dispatchEvent(new CustomEvent('saik:rings-refresh')); } catch { /* ignore */ }
    // Unlock audio SYNCHRONOUSLY inside this click. Browsers only honour an
    // AudioContext unlock while the gesture is still "active"; starting the
    // session from a deferred timer (or after an SPA navigation) loses the
    // gesture, the context begins suspended, and the FIRST reply is silent
    // (owner report 2026-10-08: "her first response did not play as audio").
    try {
      const Ctx: typeof AudioContext | undefined =
        (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Ctx) { const c = new Ctx(); void c.resume().then(() => c.close().catch(() => {})).catch(() => {}); }
    } catch { /* ignore */ }
    // Start live voice NOW, still inside the gesture, then navigate. The listen
    // widget is mounted app-wide so it survives the route change.
    // CONSOLIDATION (owner report 2026-10-13): hand the answer to the widget so
    // the agent's reason is spoken by the ONE call experience; then start live.
    try {
      window.dispatchEvent(new CustomEvent('saik:ring-answer', {
        detail: { id: r.id, name: '', body: r.body || r.title || '', url: r.url },
      }));
    } catch { /* ignore */ }
    try { window.dispatchEvent(new CustomEvent('saik:start-live')); } catch { /* ignore */ }
    navigate(resolveNotificationUrl(r.url || '/app/chat'));
  };
  const dismiss = () => {
    const r = ring;
    stop();
    try { localStorage.setItem('saik_last_ring_id', String(r.id ?? '')); } catch { /* ignore */ }
    // Declining a ring SNOOZES it server-side (owner directive 2026-10-10): the
    // agent is proactive, so the ring is maintained, not removed.
    if (r.id != null) { void ringsApi.snooze(r.id).catch(() => {}); }
    try { window.dispatchEvent(new CustomEvent('saik:rings-refresh')); } catch { /* ignore */ }
    window.dispatchEvent(new CustomEvent('saik:ring-dismissed', { detail: r }));
    // Declining pops the agent up (owner directive 2026-10-10): open the widget
    // so you can talk/type — but do NOT start a voice call, and do NOT navigate;
    // you stay on the regular dashboard.
    try { window.dispatchEvent(new CustomEvent('saik:pop-agent', { detail: r })); } catch { /* ignore */ }
  };

  // The user said YES to enabling the widget so the call can start.
  const enableAndAnswer = async () => {
    const r = ringRef.current || ring;
    setAskEnable(false);
    try {
      const { assistantApi } = await import('../api/assistant');
      await assistantApi.updatePrefs({ enabled: true });
      window.dispatchEvent(new CustomEvent('saik:widget-enabled', { detail: { enabled: true } }));
    } catch { /* the call still proceeds; the widget enabling may retry next load */ }
    // Reload-free: the widget mounts via VirtualAssistantProvider prefs. Nudge it
    // to refresh, then start the call as usual.
    try { window.dispatchEvent(new CustomEvent('saik:refresh-prefs')); } catch { /* ignore */ }
    if (r) {
      try {
        window.dispatchEvent(new CustomEvent('saik:ring-answer', {
          detail: { id: r.id, name: '', body: r.body || r.title || '', url: r.url },
        }));
      } catch { /* ignore */ }
      try { window.dispatchEvent(new CustomEvent('saik:start-live')); } catch { /* ignore */ }
      navigate(resolveNotificationUrl(r.url || '/app/chat'));
    }
    setRing(null);
  };

  const declineEnable = () => {
    // No thanks — dismiss the prompt; the ring was already stopped.
    if (ringRef.current?.id != null) { void ringsApi.snooze(ringRef.current.id).catch(() => {}); }
    setAskEnable(false);
    setRing(null);
  };

  if (askEnable) {
    return (
      <div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-5 bg-slate-950/95 px-6 text-center text-white backdrop-blur"
        role="dialog" aria-modal="true" aria-label="Turn on the assistant widget to take this call" data-agent-id="ring.enable-prompt"
      >
        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-brand-600 text-3xl">🤖</div>
        <div className="max-w-sm space-y-2">
          <h2 className="text-lg font-semibold">Turn on the assistant to take this call?</h2>
          <p className="text-sm text-slate-300">
            Your assistant widget is currently off. Turn it on to answer {ring?.title ? `“${ring.title}”` : 'this call'} and talk.
          </p>
        </div>
        <div className="mt-2 flex items-center gap-4">
          <button onClick={declineEnable} data-agent-id="ring.enable-decline"
            className="rounded-full bg-slate-700 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-600">
            No thanks
          </button>
          <button onClick={enableAndAnswer} data-agent-id="ring.enable-accept"
            className="rounded-full bg-green-600 px-6 py-3 text-sm font-semibold text-white hover:bg-green-500">
            Turn on &amp; answer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 bg-slate-950/95 px-6 text-center text-white backdrop-blur"
      role="dialog" aria-modal="true" aria-label="The assistant is ringing" data-agent-id="ring.overlay"
    >
      <div className="relative flex h-28 w-28 items-center justify-center">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-500/30" />
        <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-brand-600 text-3xl">🤖</span>
      </div>
      <div className="max-w-md space-y-2">
        <p className="text-xs font-semibold uppercase tracking-widest text-brand-300">
          Incoming — the assistant needs you
        </p>
        <h2 className="text-xl font-semibold">{ring.title || 'The assistant needs you'}</h2>
        {ring.body && <p className="text-sm text-slate-300">{ring.body}</p>}
        <p className="text-xs text-slate-400" data-agent-id="ring.countdown">Ringing… {countdown}s</p>
      </div>
      <div className="mt-2 flex items-center gap-6">
        <button onClick={dismiss} data-agent-id="ring.dismiss"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white hover:bg-red-500 focus:outline-none focus:ring-2 focus:ring-white/60" aria-label="Dismiss">
          <PhoneOff size={24} aria-hidden="true" />
        </button>
        <button onClick={answer} data-agent-id="ring.answer"
          className="flex items-center gap-2 rounded-full bg-green-600 px-6 py-4 font-semibold text-white hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-white/60">
          <Phone size={20} aria-hidden="true" /> Answer
        </button>
      </div>
    </div>
  );
}
